import { KpiTile } from '@lms/charts';
import { useMemo } from 'react';
import { useLocale, useTranslations } from 'use-intl';
import type { Site } from '@/features/branding/brand-provider';
import { usePeriodLabel } from '@/features/dashboard/components/stats-layout';
import {
  countWithCompletions,
  groupsOfType,
  useCoverage,
} from '@/features/dashboard/coverage';

/**
 * Quick stats above the groups list for this year (the dashboard's default
 * period): completed trainings and how many groups have completions.
 * Renders nothing until loaded, so it never holds up the list.
 */
export function GroupQuickStats({ site }: { site: Site }) {
  const t = useTranslations();
  const locale = useLocale();
  const fromDate = useMemo(() => new Date(), []);
  const { data } = useCoverage(site.id, locale, { period: 'year', fromDate });
  const periodLabel = usePeriodLabel('year', fromDate);
  if (!data) return null;

  const completed = data.cells.reduce((sum, c) => sum + c.completed, 0);
  const tenants = groupsOfType(data, 'tenant').length;
  const suppliers = groupsOfType(data, 'supplier').length;

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <KpiTile
        label={t('stats.completed_trainings')}
        description={periodLabel}
        value={completed}
        locale={locale}
        className={suppliers > 0 ? 'sm:col-span-2 lg:col-span-1' : undefined}
      />
      <KpiTile
        label={t(`group_alias.${site.groupAlias}`)}
        description={t('stats.with_completions')}
        value={countWithCompletions(data, 'tenant')}
        of={tenants}
        locale={locale}
      />
      {suppliers > 0 && (
        <KpiTile
          label={t('group_alias.suppliers')}
          description={t('stats.with_completions')}
          value={countWithCompletions(data, 'supplier')}
          of={suppliers}
          locale={locale}
        />
      )}
    </div>
  );
}
